import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Calendar as CalendarIcon, Loader2, FileSpreadsheet, FilterX, Search, CalendarClock, X, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AgendaWeekGrid, AgendaDayList } from "../components/agenda/AgendaViews";
import { ViewToggle, WeekNav, mondayOf, addDays, weekDays, fmtLong, defaultView } from "../components/agenda/AgendaControls";
import { Counters } from "../components/agenda/Counters";

const inputCls = "px-3 py-2 text-sm rounded-md border outline-none";
const inputStyle = { borderColor: "var(--border)", background: "#fff" };

export default function Calendario() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [squadre, setSquadre] = useState([]);
  const [q, setQ] = useState("");
  const [squadraId, setSquadraId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [week, setWeek] = useState(() => mondayOf());
  const [view, setView] = useState(() => defaultView("calendario_view"));
  const [moveTarget, setMoveTarget] = useState(null);
  const debounce = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const fx = showFilters ? "" : "hidden md:block";

  const periodMode = !!(dateFrom || dateTo);
  const params = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (squadraId) p.set("squadra_id", squadraId);
    if (periodMode) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else {
      p.set("date_from", week);
      p.set("date_to", addDays(week, 6));
    }
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendario?${params()}`);
      setItems(data.items || []);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/squadre").then((s) => setSquadre(s.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line
  }, [q, squadraId, dateFrom, dateTo, week]);

  const hasFilters = !!(q || squadraId || dateFrom || dateTo);
  const clearFilters = () => { setQ(""); setSquadraId(""); setDateFrom(""); setDateTo(""); };
  const changeView = (v) => { setView(v); localStorage.setItem("calendario_view", v); };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/calendario/export/xlsx?${params()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.href = url; a.download = `calendario_${ts}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Esportati ${items.length} appuntamenti`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const effView = periodMode ? "lista" : view;
  const days = periodMode ? [...new Set(items.map((i) => i.data))].sort() : weekDays(week);
  const cardProps = { colorBy: "squadra", onReschedule: isAdmin ? setMoveTarget : undefined };
  const rangeLabel = periodMode
    ? `Periodo: ${dateFrom ? fmtLong(dateFrom) : "inizio"} – ${dateTo ? fmtLong(dateTo) : "fine"}`
    : `${fmtLong(week)} – ${fmtLong(addDays(week, 6))}`;

  return (
    <div data-testid="calendario-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "var(--primary)" }}>
            <CalendarIcon className="w-3.5 h-3.5" /> Installazioni e interventi
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Calendario</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }} data-testid="cal-count">
            {loading ? "…" : `${items.length} appuntament${items.length === 1 ? "o" : "i"}`}
            {hasFilters && <span> · <span style={{ color: "var(--accent)" }}>filtrati</span></span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewToggle view={effView} onChange={changeView} gridDisabled={periodMode} />
          <button onClick={exportXlsx} disabled={items.length === 0 || loading} className="btn-primary disabled:opacity-50" data-testid="cal-export-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Esporta Excel
          </button>
        </div>
      </div>

      <div className="card-shell p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-muted)" }} />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente: nome, ID, telefono, città…"
              className={`w-full pl-9 pr-3 ${inputCls.replace("px-3 ", "")}`} style={inputStyle} data-testid="cal-search" />
          </div>
          <button type="button" onClick={() => setShowFilters((v) => !v)} className="md:hidden btn-secondary justify-center w-full" data-testid="cal-toggle-filters">
            <SlidersHorizontal className="w-4 h-4" /> {showFilters ? "Nascondi filtri" : "Squadra e periodo"}
          </button>
          <select value={squadraId} onChange={(e) => setSquadraId(e.target.value)} className={`${fx} ${inputCls}`} style={inputStyle} data-testid="cal-filter-squadra">
            <option value="">Tutte le squadre</option>
            {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={`${fx} ${inputCls}`} style={inputStyle} title="Dal" data-testid="cal-date-from" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={`${fx} ${inputCls}`} style={inputStyle} title="Al" data-testid="cal-date-to" />
        </div>
        {hasFilters && (
          <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--border)" }}>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{periodMode && "Con il filtro Dal/Al attivo la navigazione per settimana è sospesa."}</span>
            <button onClick={clearFilters} className="btn-secondary text-xs" data-testid="cal-clear-filters">
              <FilterX className="w-3.5 h-3.5" /> Cancella filtri
            </button>
          </div>
        )}
      </div>

      <WeekNav disabled={periodMode} label={rangeLabel}
        onPrev={() => setWeek(addDays(week, -7))} onNext={() => setWeek(addDays(week, 7))} onToday={() => setWeek(mondayOf())}>
        <Counters items={items} />
      </WeekNav>

      {loading && items.length === 0 ? (
        <div className="card-shell p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
      ) : periodMode && items.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <CalendarIcon className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="cal-empty">Nessun appuntamento con i filtri attuali</div>
        </div>
      ) : effView === "settimana"
        ? <AgendaWeekGrid days={days} items={items} cardProps={cardProps} />
        : <AgendaDayList days={days} items={items} cardProps={cardProps} />}

      {moveTarget && (
        <RescheduleModal item={moveTarget} squadre={squadre} onClose={() => setMoveTarget(null)} onSaved={() => { setMoveTarget(null); load(); }} />
      )}
    </div>
  );
}

function RescheduleModal({ item, squadre, onClose, onSaved }) {
  const initDate = (item.data_inizio || "").split("T")[0] || "";
  const initTime = (() => {
    const t = (item.data_inizio || "").split("T")[1] || "";
    return t ? t.slice(0, 5) : "09:00";
  })();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [newDate, setNewDate] = useState(initDate);
  const [newTime, setNewTime] = useState(initTime);
  const [newSquadra, setNewSquadra] = useState(item.squadra_id || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!newDate) { toast.error("Seleziona una data"); return; }
    setSaving(true);
    try {
      await api.patch(`/clienti/${item.cliente_id}/pianificazione/move`, {
        new_date: newDate,
        new_time: newTime || null,
        new_squadra_id: newSquadra,
      });
      toast.success("Pianificazione aggiornata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="cal-reschedule-modal">
      <div className="card-shell w-full max-w-md p-6" style={{ background: "#fff" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--primary)" }}>
              <CalendarClock className="w-3.5 h-3.5" /> Riprogramma
            </div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{item.cliente_nome}</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-black/5" data-testid="cal-reschedule-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Data</label>
              <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)" }} data-testid="cal-reschedule-date" />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Ora inizio</label>
              <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)" }} data-testid="cal-reschedule-time" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>Squadra</label>
            <select value={newSquadra} onChange={(e) => setNewSquadra(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="cal-reschedule-squadra">
              <option value="">— Nessuna squadra —</option>
              {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="cal-reschedule-cancel">Annulla</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="cal-reschedule-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
